import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { ResolvedIcon } from "@/features/adp/components/shared/ResolvedIcon";
import { useRegisteredDestinations } from "@/features/adp/queries/useDestinationQueries";
import { closeOverlay, openOverlay, type DestinationRegisterationOverlayData } from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Input } from "@/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Skeleton } from "@/ui/skeleton";
import { ChevronLeftIcon, ChevronRightIcon, Filter, Search } from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import styles from "./DestinationRegisterationDialog.module.scss";

const DEFAULT_ITEMS_PER_PAGE = 36;
const ITEMS_PER_PAGE_OPTIONS = [12, 24, 36, 48];

function isDestinationRegisterationOverlayData(
  value: unknown
): value is DestinationRegisterationOverlayData {
  return Boolean(value && typeof value === "object");
}

const DestinationRegisterationDialog = () => {
  const dispatch = useAppDispatch();
  const { isOpen, type, data } = useAppSelector((state) => state.overlay);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [searchValue, setSearchValue] = useState("");
  const [lastTotalCount, setLastTotalCount] = useState(0);

  const isVisible = isOpen && type === "destination-registeration-modal";
  const modalData = isVisible && isDestinationRegisterationOverlayData(data) ? data : null;
  const allowedInstance = modalData?.data_source_instance ?? modalData?.sourceType ?? undefined;

  const { data: registeredDestinationsResponse, isPending } = useRegisteredDestinations({
    allowed_instances: allowedInstance,
    target_name: searchValue,
    enabled: isVisible,
    page,
    limit: itemsPerPage,
  });

  const registeredDestinations = registeredDestinationsResponse?.targets ?? [];
  const visibleDestinations = useMemo(() => {
    if (registeredDestinations.length <= itemsPerPage) {
      return registeredDestinations;
    }

    const startIndex = (page - 1) * itemsPerPage;
    return registeredDestinations.slice(startIndex, startIndex + itemsPerPage);
  }, [itemsPerPage, page, registeredDestinations]);
  const totalCount = registeredDestinationsResponse?.total ?? lastTotalCount;
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / itemsPerPage));
  }, [itemsPerPage, totalCount]);

  useEffect(() => {
    if (isVisible) {
      setPage(1);
      setSearchValue("");
    }
  }, [allowedInstance, isVisible]);

  useEffect(() => {
    if (registeredDestinationsResponse && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, registeredDestinationsResponse, totalPages]);

  useEffect(() => {
    if (registeredDestinationsResponse) {
      setLastTotalCount(registeredDestinationsResponse.total ?? 0);
    }
  }, [registeredDestinationsResponse]);

  const handleClose = () => dispatch(closeOverlay());

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setPage(1);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  const handleRegisteredDestinationClick = (destination: { target_data_source_id?: number; data_source_name: string; database_name?: string }) => {
    dispatch(openOverlay({
      type: "registered-destinations-details",
      data: {
        nodeId: modalData?.nodeId,
        destinationName: destination.data_source_name,
        databaseName: destination.database_name,
        schemaName: null,
        targetDataSourceId: destination.target_data_source_id ?? null,
      },
    }));
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className="max-w-3xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className={styles.header}>
          <DialogTitle className={styles.title}>Select a data Destination</DialogTitle>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <p className={styles.description}>
            Select an existing destination to begin configuring.
          </p>

          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <Search className={styles.searchIcon} />
              <Input
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search Source"
                aria-label="Search destination"
                className={styles.searchInput}
              />
            </div>

            <Button
              type="button"
              variant="outline"
              size="icon"
              className={styles.filterButton}
              aria-label="Filter destinations"
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          <div className={styles.grid}>
            {isPending &&
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <Card key={`registered-destination-skeleton-${index}`} className={styles.card}>
                  <Skeleton className={styles.iconSkeleton} />
                  <div className={styles.sourceText}>
                    <Skeleton className={styles.nameSkeleton} />
                    <Skeleton className={styles.instanceSkeleton} />
                  </div>
                </Card>
              ))}

            {!isPending &&
              visibleDestinations.map((destination) => (
                <Card
                  key={destination.target_data_source_id ?? destination.data_source_name}
                  className={styles.card}
                  onClick={() => handleRegisteredDestinationClick(destination)}
                >
                  <div className={styles.iconBox}>
                    <ResolvedIcon
                      imageLink={destination.image_link}
                      alt={destination.data_source_name}
                      className={styles.sourceIcon}
                    />
                  </div>

                  <div className={styles.sourceText}>
                    <p className={styles.sourceName}>{destination.data_source_name}</p>
                    <p className={styles.sourceInstance}>{destination.data_source_instance}</p>
                  </div>
                </Card>
              ))}

            {!isPending && visibleDestinations.length === 0 && (
              <div className={styles.emptyState}>No destinations found.</div>
            )}
          </div>

          <div className={styles.paginationBar}>
            <div className={styles.paginationGroup}>
              <span className={styles.paginationLabel}>Items per page</span>
              <Select value={String(itemsPerPage)} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className={styles.rowsSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className={styles.paginationGroup}>
              <span className={styles.pageText}>
                Page {page} of {totalPages}
              </span>
              <div className={styles.pageControls}>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page <= 1}
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((currentPage) => Math.min(totalPages, currentPage + 1))
                  }
                  aria-label="Next page"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default DestinationRegisterationDialog;
